import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useOrg } from '@/context/OrgContext'
import { useCreateAsset, useUpdateAsset } from '@/hooks'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import type { Asset } from '@/types'

const assetFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
  description: z.string().max(500, 'Description is too long').optional(),
})

type AssetFormValues = z.infer<typeof assetFormSchema>

interface AssetFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  asset?: Asset
}

export function AssetFormDialog({
  open,
  onOpenChange,
  asset,
}: AssetFormDialogProps) {
  const { effectiveOrgId } = useOrg()
  const isEditing = !!asset

  const createMutation = useCreateAsset(effectiveOrgId)
  const updateMutation = useUpdateAsset(asset?.id ?? '', effectiveOrgId)

  const form = useForm<AssetFormValues>({
    resolver: zodResolver(assetFormSchema),
    defaultValues: {
      name: asset?.name ?? '',
      description: asset?.description ?? '',
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        name: asset?.name ?? '',
        description: asset?.description ?? '',
      })
    }
  }, [open, asset, form])

  const isPending = createMutation.isPending || updateMutation.isPending

  function onSubmit(values: AssetFormValues) {
    const data = {
      name: values.name,
      description: values.description || undefined,
    }

    if (isEditing) {
      updateMutation.mutate(data, {
        onSuccess: () => {
          onOpenChange(false)
        },
      })
    } else {
      createMutation.mutate(data, {
        onSuccess: () => {
          onOpenChange(false)
        },
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Asset' : 'Add Asset'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the asset details below.'
              : 'Enter the details for the new asset template.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Asset name (e.g., Toilet Paper)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional description of the asset"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? isEditing
                    ? 'Saving...'
                    : 'Creating...'
                  : isEditing
                    ? 'Save Changes'
                    : 'Add Asset'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
